import type { PartialBlock } from '@blocknote/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchPageDocument } from '@/lib/blocks/api';
import {
  getCachedPageDocument,
  invalidatePageDocument,
  type PageDocument,
} from '@/lib/blocks/page-cache';
import type { BlockRecord } from '@/lib/blocks/types';

import { blocksToPartialBlocks } from './blocknote-types';
import { resolveFileUrlsForEditor, stripFileUrlsForStorage } from './file-block-urls';
import { blocksContentEqual, repairPartialBlocks } from './sanitize-initial-content';

export type PageLoadState = {
  loading: boolean;
  error: string | null;
  block: BlockRecord | null;
  contentBlocks: BlockRecord[];
  initialContent: PartialBlock[];
  syncVersions: Map<string, number>;
  contentRevision: number;
  reload: () => void;
  reloadFromRemote: () => void;
};

function documentToState(
  document: PageDocument,
): Omit<PageLoadState, 'loading' | 'error' | 'reload' | 'reloadFromRemote' | 'contentRevision'> {
  const versions = new Map<string, number>();
  for (const row of document.contentBlocks) {
    versions.set(row.id, row.syncVersion);
  }
  return {
    block: document.block,
    contentBlocks: document.contentBlocks,
    initialContent: blocksToPartialBlocks(document.block.id, document.contentBlocks),
    syncVersions: versions,
  };
}

async function documentToEditorState(
  document: PageDocument,
): Promise<
  Omit<PageLoadState, 'loading' | 'error' | 'reload' | 'reloadFromRemote' | 'contentRevision'>
> {
  const base = documentToState(document);
  console.log(
    '[CS-IMG] page-load:server contentBlocks',
    document.contentBlocks
      .filter((b) => b.type === 'image' || b.type === 'file')
      .map((b) => ({ id: b.id, type: b.type, content: b.content })),
  );
  const withUrls = await resolveFileUrlsForEditor(base.initialContent);
  const { blocks: initialContent } = repairPartialBlocks(withUrls);
  console.log(
    '[CS-IMG] page-load:editor initialContent images',
    initialContent
      .flatMap(function flatten(b): PartialBlock[] {
        return [b, ...(b.children ?? []).flatMap(flatten)];
      })
      .filter((b) => b.type === 'image' || b.type === 'file')
      .map((b) => ({ id: b.id, type: b.type, props: b.props })),
  );
  return {
    ...base,
    initialContent,
  };
}

export function usePage(pageId: string | undefined): PageLoadState {
  const cached = pageId ? getCachedPageDocument(pageId) : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [block, setBlock] = useState<BlockRecord | null>(cached?.block ?? null);
  const [contentBlocks, setContentBlocks] = useState<BlockRecord[]>(cached?.contentBlocks ?? []);
  const [initialContent, setInitialContent] = useState<PartialBlock[]>([]);
  const [syncVersions, setSyncVersions] = useState<Map<string, number>>(() => {
    const versions = new Map<string, number>();
    if (cached) {
      for (const row of cached.contentBlocks) {
        versions.set(row.id, row.syncVersion);
      }
    }
    return versions;
  });
  const [reloadToken, setReloadToken] = useState(0);
  const [contentRevision, setContentRevision] = useState(0);
  const silentReloadRef = useRef(false);
  const initialContentRef = useRef(initialContent);
  initialContentRef.current = initialContent;

  const reload = useCallback(() => {
    silentReloadRef.current = false;
    setReloadToken((t) => t + 1);
  }, []);

  const reloadFromRemote = useCallback(() => {
    silentReloadRef.current = true;
    setReloadToken((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!pageId) {
      setLoading(false);
      setError('Missing page id');
      return;
    }

    let cancelled = false;
    const silent = silentReloadRef.current;
    silentReloadRef.current = false;

    if (reloadToken > 0) {
      invalidatePageDocument(pageId);
    }

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    void (async () => {
      try {
        const snapshot = reloadToken === 0 ? getCachedPageDocument(pageId) : null;
        const document = snapshot ?? (await fetchPageDocument(pageId));
        if (cancelled) {
          return;
        }

        const next = await documentToEditorState(document);
        if (cancelled) {
          return;
        }

        setBlock(next.block);
        setContentBlocks(next.contentBlocks);
        setSyncVersions(next.syncVersions);

        if (reloadToken > 0) {
          const contentChanged = !blocksContentEqual(
            stripFileUrlsForStorage(next.initialContent),
            stripFileUrlsForStorage(initialContentRef.current),
          );
          if (contentChanged) {
            setInitialContent(next.initialContent);
            setContentRevision((r) => r + 1);
          }
        } else {
          setInitialContent(next.initialContent);
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load page');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageId, reloadToken]);

  return {
    loading,
    error,
    block,
    contentBlocks,
    initialContent,
    syncVersions,
    contentRevision,
    reload,
    reloadFromRemote,
  };
}
